import React from "react";
import { useQuery } from "@tanstack/react-query";
import FloatingSlideOutSidebar from "../../../../ui-components/floating-slide-out-sidebar/floatingSlideOutSidebar";
import Loading from "../../../../ui-components/loading/loading";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import CommunicationsService from "../../../../app/data/communications/communicationsService";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "../utils/pickupInboxQueryOptions";
import EmailHeaderPanel from "../../../common/email-viewer/EmailHeaderPanel";
import ServiceRequestCollapsible from "./serviceRequestCollapsible";

const communicationsService = CommunicationsService.getInstance();

export const MESSAGE_DETAIL_FLOATING_STORAGE_KEY = "pickup-inbox-message-detail";

export interface MessageDetailSidebarProps {
  messageId: number | null;
  show: boolean;
  onClose: () => void;
  expandPickupRequestId?: number | null;
  onViewEmail: () => void;
  onPickupNumberClick: (pickupNumber: string) => void;
  onReviewComplete?: () => void;
}

const MessageDetailSidebar: React.FC<MessageDetailSidebarProps> = ({
  messageId,
  show,
  onClose,
  expandPickupRequestId,
  onViewEmail,
  onPickupNumberClick,
  onReviewComplete,
}) => {
  const { data: message, isLoading, isFetching, isError } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_MESSAGE_DETAIL, messageId],
    queryFn: () => communicationsService.getInboundMessage(messageId!),
    enabled: show && messageId != null,
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const showDetailLoading = isLoading || isFetching;

  return (
    <FloatingSlideOutSidebar
      storageKey={MESSAGE_DETAIL_FLOATING_STORAGE_KEY}
      header={message?.subject || "Email details"}
      onClose={onClose}
      show={show}
      size="medium"
      spinner={showDetailLoading}
      className="xgs-pickup-inbox__detail-sidebar"
    >
      <div className="xgs-pickup-inbox__detail">
        {showDetailLoading && (
          <div className="xgs-pickup-inbox__detail--loading">
            <Loading isLoading />
          </div>
        )}
        {isError && !showDetailLoading && (
          <div className="xgs-pickup-inbox__detail-error">Unable to load message details.</div>
        )}
        {message && !showDetailLoading && (
          <>
            <EmailHeaderPanel
              sender={message.sender}
              receiver={message.receiver}
              subject={message.subject}
              receivedAt={message.receivedAt}
              processingState={message.processingState}
              pendingReviewCount={message.summary.pendingReviewCount}
              webLink={message.webLink}
              onViewEmail={onViewEmail}
              viewEmailAsLink
            />

            <h3 className="xgs-pickup-inbox__detail-section-title">Service requests</h3>
            {message.serviceRequests.length === 0 ? (
              <p className="xgs-pickup-inbox__detail-empty">No service requests on this email.</p>
            ) : (
              message.serviceRequests.map((sr) => (
                <ServiceRequestCollapsible
                  key={sr.id}
                  serviceRequest={sr}
                  senderEmail={message.sender}
                  defaultExpanded={
                    expandPickupRequestId != null &&
                    sr.pickupRequest?.id === expandPickupRequestId
                  }
                  onPickupNumberClick={onPickupNumberClick}
                  onReviewComplete={onReviewComplete}
                />
              ))
            )}
          </>
        )}
      </div>
    </FloatingSlideOutSidebar>
  );
};

export default MessageDetailSidebar;
